import { nextTick, ref } from 'vue'
import type { Ref } from 'vue'
import { moveInArray } from '@/utils/galleries/reorder'

export interface GridReorderLabels {
  lifted: (position: number, total: number) => string
  moved: (position: number, total: number) => string
  dropped: (position: number, total: number) => string
  cancelled: () => string
}

export interface GridReorderOptions {
  /** Current order of the draggable ids. */
  getIds: () => string[]
  /** Number of grid columns, used for Up/Down keyboard moves. */
  getColumns: () => number
  onReorder: (ids: string[]) => void
  labels: GridReorderLabels
  /** Attribute name used to find the handle element again after a keyboard move. */
  handleAttribute?: string
}

export interface UseGridReorder {
  liftedId: Ref<string | null>
  dragOverId: Ref<string | null>
  draggingId: Ref<string | null>
  announcement: Ref<string>
  onDragStart: (id: string, event: DragEvent) => void
  onDragOver: (id: string, event: DragEvent) => void
  onDrop: (id: string, event: DragEvent) => void
  onDragEnd: () => void
  onHandleKeydown: (id: string, event: KeyboardEvent) => void
}

/**
 * Native HTML5 drag-and-drop plus a keyboard alternative: Space/Enter lifts the focused handle,
 * arrow keys move it (Up/Down jump a whole row), Space/Enter drops it and Escape cancels.
 */
export function useGridReorder(options: GridReorderOptions): UseGridReorder {
  const liftedId = ref<string | null>(null)
  const dragOverId = ref<string | null>(null)
  const draggingId = ref<string | null>(null)
  const announcement = ref('')
  const handleAttribute = options.handleAttribute ?? 'data-reorder-handle'
  let originalIds: string[] = []

  function onDragStart(id: string, event: DragEvent): void {
    draggingId.value = id
    if (event.dataTransfer !== null) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', id)
    }
  }

  function onDragOver(id: string, event: DragEvent): void {
    if (draggingId.value === null) return
    event.preventDefault()
    if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'move'
    dragOverId.value = id
  }

  function onDrop(id: string, event: DragEvent): void {
    event.preventDefault()
    const dragged = draggingId.value
    draggingId.value = null
    dragOverId.value = null
    if (dragged === null || dragged === id) return
    const ids = options.getIds()
    const to = ids.indexOf(id)
    const from = ids.indexOf(dragged)
    if (to === -1 || from === -1) return
    options.onReorder(moveInArray(ids, from, to))
  }

  function onDragEnd(): void {
    draggingId.value = null
    dragOverId.value = null
  }

  async function refocus(id: string): Promise<void> {
    await nextTick()
    const handles = document.querySelectorAll<HTMLElement>(`[${handleAttribute}]`)
    Array.from(handles).find((el) => el.getAttribute(handleAttribute) === id)?.focus()
  }

  function onHandleKeydown(id: string, event: KeyboardEvent): void {
    const ids = options.getIds()

    if (liftedId.value !== id) {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        liftedId.value = id
        originalIds = [...ids]
        announcement.value = options.labels.lifted(ids.indexOf(id) + 1, ids.length)
      }
      return
    }

    const index = ids.indexOf(id)
    const columns = Math.max(1, options.getColumns())
    let delta = 0
    if (event.key === 'ArrowLeft') delta = -1
    else if (event.key === 'ArrowRight') delta = 1
    else if (event.key === 'ArrowUp') delta = -columns
    else if (event.key === 'ArrowDown') delta = columns

    if (delta !== 0) {
      event.preventDefault()
      const target = Math.max(0, Math.min(ids.length - 1, index + delta))
      if (target !== index) {
        options.onReorder(moveInArray(ids, index, target))
        announcement.value = options.labels.moved(target + 1, ids.length)
        void refocus(id)
      }
      return
    }

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      liftedId.value = null
      announcement.value = options.labels.dropped(index + 1, ids.length)
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      liftedId.value = null
      options.onReorder(originalIds)
      announcement.value = options.labels.cancelled()
      void refocus(id)
    }
  }

  return {
    liftedId,
    dragOverId,
    draggingId,
    announcement,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onHandleKeydown,
  }
}
